import { useEffect, useRef } from 'react'
import { Chip, Popover, Stack } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { DocumentChip } from '@/types/documents'
import { getCheckboxFormattedValue } from '@/utils/data-type'
import { useSelectedFieldContext } from './providers/SelectedFieldProvider'

type OverlappingChipsPopoverProps = {
  chips: DocumentChip[]
  overlay: OverlayState
  anchorPosition: { left: number; top: number }
  onChipClick: (chip: DocumentChip) => void
}

export default function OverlappingChipsPopover({
  chips,
  overlay,
  anchorPosition,
  onChipClick,
}: OverlappingChipsPopoverProps) {
  const { selectedField, isSelectedField } = useSelectedFieldContext() || {}
  const selectedFieldRef = useRef({ old: selectedField })

  useEffect(() => {
    if (!selectedFieldRef.current.old)
      selectedFieldRef.current.old = selectedField
  }, [selectedField])

  useEffect(() => {
    if (
      selectedFieldRef.current.old &&
      selectedFieldRef.current.old !== selectedField
    ) {
      overlay.close()
      selectedFieldRef.current.old = selectedField
    }
  }, [overlay, selectedField])

  return (
    <Popover
      open={overlay.isOpen}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={overlay.close}
      disableAutoFocus={!!selectedField}
      disableRestoreFocus={!selectedField}
      slotProps={{
        paper: {
          elevation: 4,
          onMouseDown: (e) => e.preventDefault(),
          // zIndex used to match canvas zIndex
          sx: { p: 1, zIndex: 4 },
        },
        root: {
          slotProps: {
            backdrop: {
              onMouseDown: (e) => {
                // Prevent clicking backdrop from also causing selected field to lose focus [sc-7297]
                e.preventDefault()
              },
            },
          },
        },
      }}
    >
      <Stack spacing={0.5} sx={{ width: '100%' }}>
        {chips?.map((chip) => {
          const isSelected = !!isSelectedField?.(
            chip.project_grid_field_id,
            chip.document_row_id,
          )

          const label = getCheckboxFormattedValue(chip._text, undefined, true)
          return (
            <Chip
              key={chip.id}
              id={chip.id}
              sx={{
                minWidth: '50px',
                width: 'fit-content',
                // Aligns checkbox and delete icons
                '.MuiChip-label': { display: 'flex' },
              }}
              label={label}
              clickable
              onClick={() => onChipClick(chip)}
              onMouseDown={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
                e.preventDefault()
              }
              color={isSelected ? 'primary' : 'neutral'}
              variant={chip.project_grid_field_id ? 'filled' : 'outlined'}
            />
          )
        })}
      </Stack>
    </Popover>
  )
}
